import React, { useEffect, useMemo, useState } from 'react';
import { Play } from 'lucide-react';
import AdvertSection from './partials/AdvertSection';
import EventTypeTitle from './EventTypeTitle';
import { VIRTUAL_ROOM_COUNT } from '../config/virtualRooms';
import { LIVESTREAM_GENRES, LIVESTREAM_LANGUAGES } from '../config/livestreamConstants';
import {
  getNowLiveEventsByRoom,
  getStartingSoonEventsByRoom,
  matchesEventTypeFilter,
  matchesLivestreamCategoryFilter,
} from '../utils/eventRoomHelpers';
import { useT } from '../i18n/I18nModule';

const API = process.env.REACT_APP_API_URL || '';

function joinLiveEvent(ev, user) {
  if (!ev?.id) return;
  const payload = user?.id ? { action: 'login', userId: user.id } : { action: 'login' };
  fetch(`${API}/api/events/${encodeURIComponent(ev.id)}/logs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
  window.location.href = `/event/stream/${ev.id}`;
}

function DashboardEventsTable({
  upcomingEvents,
  onOpenEvent,
  formatEventDay,
  user,
  AddButton,
  typeFilter,
  setTypeFilter,
  categoryFilter,
  setCategoryFilter,
}) {
  const t = useT();
  const showCategoryFilter =
    typeFilter === 'livestream:music' || typeFilter === 'livestream:talk';
  const categoryOptions =
    typeFilter === 'livestream:talk' ? LIVESTREAM_LANGUAGES : LIVESTREAM_GENRES;

  return (
    <section className="Dashboard-events">
      {AddButton ? <AddButton /> : null}
      <div className="Dashboard-events-header">
        <h3>{t('dashboard.upcomingEvents')}</h3>
        <hr />
      </div>

      <div className="Dashboard-events-filters">
        <label className="Dashboard-events-filter">
          <span className="Dashboard-events-filter-label">{t('dashboard.type')}</span>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCategoryFilter('all');
            }}
            className="Dashboard-events-filter-select"
            aria-label={t('dashboard.type')}
          >
            <option value="all">{t('common.all')}</option>
            <option value="game">{t('dashboard.filterGame')}</option>
            <option value="chat">{t('dashboard.filterChat')}</option>
            <option value="movie">{t('dashboard.filterMovie')}</option>
            <option value="livestream:music">{t('dashboard.filterLiveMusic')}</option>
            <option value="livestream:talk">{t('dashboard.filterLiveTalk')}</option>
          </select>
        </label>
        {showCategoryFilter && (
          <label className="Dashboard-events-filter">
            <span className="Dashboard-events-filter-label">
              {typeFilter === 'livestream:talk' ? t('dashboard.language') : t('dashboard.genre')}
            </span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="Dashboard-events-filter-select"
              aria-label={typeFilter === 'livestream:talk' ? t('dashboard.language') : t('dashboard.genre')}
            >
              <option value="all">{t('common.all')}</option>
              {categoryOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="Dashboard-events-table-wrap">
        <table className="Dashboard-events-table">
          <thead>
            <tr>
              <th>{t('dashboard.event')}</th>
              <th>{t('dashboard.starts')}</th>
              <th>{t('dashboard.date')}</th>
            </tr>
          </thead>
          <tbody>
            {upcomingEvents.length === 0 ? (
              <tr>
                <td colSpan={3} className="Dashboard-events-empty">
                  {t('dashboard.noEvents')}
                </td>
              </tr>
            ) : (
              upcomingEvents.map((ev) => {
                const durationStr =
                  ev.minutes != null
                    ? ev.minutes >= 60
                      ? `${ev.minutes / 60} h`
                      : `${ev.minutes} min`
                    : '—';
                const isOwnEvent =
                  user &&
                  ((ev.createdByUserId != null && String(ev.createdByUserId) === String(user.id)) ||
                    ev.author === (user.displayName || user.username));
                return (
                  <tr
                    key={ev.id}
                    className={`Dashboard-events-row-clickable${isOwnEvent ? ' Dashboard-events-row--own' : ''}`}
                  >
                    <td>
                      <button
                        type="button"
                        className="EventView-open-btn"
                        onClick={() => {
                          if (ev.id) {
                            window.location.href = isOwnEvent
                              ? `/event/edit/${ev.id}`
                              : `/event/show/${ev.id}`;
                          } else {
                            onOpenEvent(ev);
                          }
                        }}
                      >
                        <EventTypeTitle event={ev} fallback="—" />
                        {ev.enableQA ? <sup className="Event-qa" alt="Q&A enabled">?</sup> : ''}
                      </button>
                      {!isOwnEvent && (
                        <>
                          <br />
                          <i>By</i> <strong>{ev.author || '—'}</strong>
                        </>
                      )}
                    </td>
                    <td>
                      {ev.time || '—'}
                      <br />({durationStr})
                    </td>
                    {(() => {
                      const { base, label } = formatEventDay(ev.eventDate);
                      return (
                        <td>
                          {base}
                          {label && <span className="Event-day-label"> ({label})</span>}
                        </td>
                      );
                    })()}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <p className="Dashboard-note-small">Listattu aikajärjestyksessä.</p>
    </section>
  );
}

export default function DashboardPage({
  path,
  user,
  allEvents,
  upcomingEvents,
  formatEventDay,
  onOpenChat,
  onOpenCreateEvent,
  onOpenEventView,
}) {
  const t = useT();
  const isPrivileged = !!user && user.role && user.role !== 'user';
  const isAdmin = user?.role === 'admin';

  const [chatPresenceCount, setChatPresenceCount] = useState(2);
  const [forumUnreadReplies, setForumUnreadReplies] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const filteredUpcoming = useMemo(
    () =>
      upcomingEvents.filter(
        (ev) =>
          matchesEventTypeFilter(ev, typeFilter) &&
          matchesLivestreamCategoryFilter(ev, categoryFilter),
      ),
    [upcomingEvents, typeFilter, categoryFilter],
  );

  const liveByRoom = useMemo(() => getNowLiveEventsByRoom(allEvents || []), [allEvents]);
  const soonByRoom = useMemo(() => getStartingSoonEventsByRoom(allEvents || []), [allEvents]);

  useEffect(() => {
    if (isAdmin) return undefined;
    const fetchPresence = () => {
      fetch(`${API}/api/chat/presence`)
        .then((res) => res.json())
        .then((data) => setChatPresenceCount(Array.isArray(data?.present) ? data.present.length : 0) + 2)
        .catch(() => setChatPresenceCount(2));
    };
    fetchPresence();
    const interval = setInterval(fetchPresence, 10000);
    return () => clearInterval(interval);
  }, [user, isAdmin]);

  useEffect(() => {
    if (!isPrivileged || !user?.id || isAdmin) {
      setForumUnreadReplies(0);
      return undefined;
    }
    const uid = String(user.id).replace(/n$/, '');
    const fetchUnread = () => {
      fetch(`${API}/api/forum/unread-replies-count?userId=${encodeURIComponent(uid)}`)
        .then((res) => (res.ok ? res.json() : Promise.reject()))
        .then((data) => setForumUnreadReplies(typeof data?.count === 'number' ? data.count : 0))
        .catch(() => setForumUnreadReplies(0));
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user, isPrivileged, isAdmin]);

  const EventAddButton = () => {
    if (!user || isAdmin) return null;
    return (
      <button type="button" className="Event-add" onClick={onOpenCreateEvent} title={t('dashboard.createEvent')}>
        +
      </button>
    );
  };

  const roomActionButtons = [];
  for (let room = 1; room <= VIRTUAL_ROOM_COUNT; room++) {
    const live = liveByRoom[room];
    const soon = soonByRoom[room];
    if (live && isPrivileged && !isAdmin) {
      roomActionButtons.push(
        <button
          key={`live-${room}`}
          type="button"
          className="Button Dashboard-live-btn"
          onClick={() => joinLiveEvent(live, user)}
        >
          <Play className="Dashboard-live-btn-icon" /> &quot;{live.title || '—'}&quot; {t('event.joinNow')} @{t('event.room')} {room}
        </button>,
      );
    } else if (soon && isPrivileged && !isAdmin) {
      roomActionButtons.push(
        <button
          key={`soon-${room}`}
          type="button"
          className="Button Dashboard-soon-btn"
          onClick={() => joinLiveEvent(soon, user)}
        >
          {t('event.startsSoon')} &quot;{soon.title || '—'}&quot; @{t('event.room')} {room}
        </button>,
      );
    }
  }

  return (
    <div className="App-block">
      <div className="Advert-wrapper">
        <AdvertSection path={path} />
      </div>
      <div className={`Dashboard-actions${isAdmin ? ' Dashboard-actions--admin' : ''}`}>
        <div className="buttons">
          {!isAdmin ? (
            <>
              <button
                type="button"
                className="Button Dashboard-chat-btn"
                disabled={!isPrivileged}
                title={isPrivileged ? 'Tästä Chat-huoneeseen' : 'Tarvitset jäsenyyden'}
                onClick={onOpenChat}
              >
                <span className="Dashboard-chat-btn-text">{t('dashboard.chat')}</span>
                <span className="Dashboard-chat-badge" aria-label={`${chatPresenceCount} käyttäjää chatissa`}>
                  {chatPresenceCount + 2}
                </span>
              </button>

              <button
                type="button"
                className="Button Dashboard-forum-btn"
                onClick={() => isPrivileged && (window.location.href = '/forum/')}
                disabled={!isPrivileged}
                title={isPrivileged ? 'Ilmoitustaulu' : 'Tarvitset jäsenyyden'}
              >
                <span className="Dashboard-forum-btn-text">{t('dashboard.forum')}</span>
                {isPrivileged && forumUnreadReplies > 0 ? (
                  <span
                    className="Dashboard-forum-badge"
                    aria-label={`${forumUnreadReplies} lukematonta vastausta ilmoituksiisi`}
                  >
                    {forumUnreadReplies > 99 ? '99+' : forumUnreadReplies}
                  </span>
                ) : null}
              </button>
            </>
          ) : null}
        </div>
        <section className="Dashboard-events">
          {roomActionButtons.length > 0 ? (
            <div className="Dashboard-room-actions">{roomActionButtons}</div>
          ) : null}

          <DashboardEventsTable
            upcomingEvents={filteredUpcoming}
            formatEventDay={formatEventDay}
            onOpenEvent={onOpenEventView}
            user={user}
            AddButton={EventAddButton}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
          />
        </section>
      </div>

      {!isPrivileged ? (
        <p className="Dashboard-note">{t('dashboard.memberNote')}</p>
      ) : (
        ''
      )}
    </div>
  );
}
